// src/app/services/diagnostico/diagnostico.service.ts

import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';

const MAX_BREADCRUMBS = 15;

interface Breadcrumb {
  timestamp: string;
  tipo: 'navegacion' | 'http_error' | 'console_error';
  detalle: string;
}

export interface ContextoTecnico {
  urlOrigen: string;
  versionApp: string;
  navegador: string;
  sistemaOperativo: string;
  resolucionPantalla: string;
  infoTecnicaJson: string;
}

/**
 * Colector en memoria de "breadcrumbs": últimas navegaciones, últimos requests HTTP
 * fallidos (alimentado por GlobalHttpErrorInterceptorService) y últimos errores de
 * consola. Es lo que viaja en InfoTecnicaJson cuando el cliente reporta un problema
 * desde app-dialog-reportar-problema, para que soporte no tenga que volver a
 * preguntar "¿qué estabas haciendo cuando pasó?".
 */
@Injectable({
  providedIn: 'root'
})
export class DiagnosticoService {
  private router = inject(Router);
  private breadcrumbs: Breadcrumb[] = [];
  private consoleErrorOriginal = console.error.bind(console);
  private patched = false;

  constructor() {
    this.router.events.subscribe(evento => {
      if (evento instanceof NavigationEnd) {
        this.agregar('navegacion', evento.urlAfterRedirects);
      }
    });

    this.interceptarConsoleError();
  }

  private interceptarConsoleError(): void {
    if (this.patched) return;
    this.patched = true;

    console.error = (...args: any[]) => {
      try {
        const mensaje = args.map(a => (typeof a === 'string' ? a : this.safeStringify(a))).join(' ');
        this.agregar('console_error', mensaje.substring(0, 300));
      } catch {
        // nunca romper la app por culpa del logging
      }
      this.consoleErrorOriginal(...args);
    };
  }

  /** Llamado desde GlobalHttpErrorInterceptorService en cada error de red. */
  registrarErrorHttp(request: HttpRequest<unknown>, error: HttpErrorResponse): void {
    this.agregar('http_error', `${request.method} ${request.urlWithParams} -> ${error.status} ${error.statusText}`);
  }

  private agregar(tipo: Breadcrumb['tipo'], detalle: string): void {
    this.breadcrumbs.push({ timestamp: new Date().toISOString(), tipo, detalle });
    if (this.breadcrumbs.length > MAX_BREADCRUMBS) {
      this.breadcrumbs.shift();
    }
  }

  private safeStringify(obj: any): string {
    try {
      return JSON.stringify(obj);
    } catch {
      return String(obj);
    }
  }

  /** Arma el contexto técnico completo para adjuntar al reporte, sin pedirle nada al usuario. */
  obtenerContextoTecnico(versionApp: string): ContextoTecnico {
    const ua = navigator.userAgent;

    return {
      urlOrigen: window.location.href,
      versionApp,
      navegador: ua,
      sistemaOperativo: this.detectarSistemaOperativo(ua),
      resolucionPantalla: `${screen.width}x${screen.height} (viewport ${window.innerWidth}x${window.innerHeight})`,
      infoTecnicaJson: JSON.stringify({
        breadcrumbs: this.breadcrumbs,
        idioma: navigator.language,
        zonaHoraria: Intl.DateTimeFormat().resolvedOptions().timeZone,
        online: navigator.onLine
      })
    };
  }

  private detectarSistemaOperativo(ua: string): string {
    if (/Windows/i.test(ua)) return 'Windows';
    if (/Mac OS/i.test(ua)) return 'macOS';
    if (/Android/i.test(ua)) return 'Android';
    if (/iPhone|iPad|iOS/i.test(ua)) return 'iOS';
    if (/Linux/i.test(ua)) return 'Linux';
    return 'Desconocido';
  }
}
